// State, triage groups, and drag handlers for InboxView (lint split).
import { useEffect, useMemo, useState } from 'react';
import {
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useAuth } from '@/features/auth';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import {
  buildContextDropZonesWithPending,
  buildProjectDropZonesWithPending,
} from '@/features/organize/utils/inboxTriageUtils';
import {
  buildInboxTriageGroups,
  getDurationTriageTasks,
  getUntaggedProjectTasks,
  getUntaggedContextTasks,
} from '@/features/organize/utils/buildInboxTriageGroups';
import { useInboxPendingTags } from '@/features/organize/hooks/useInboxPendingTags';
import { useInboxViewControllerDrag } from '@/features/organize/hooks/useInboxViewControllerDrag';
import { useT } from '@/i18n/useT';

export function useInboxViewController(onContextPanelOpenChange) {
  const t = useT();
  const { user } = useAuth();
  const { filteredTasks, allTasks, projectDisplayMap, contextDisplayMap } =
    useTasksContext();
  const { updateTask } = useTaskMutationsContext();
  const [openGroup, setOpenGroup] = useState(0);
  const [activeTask, setActiveTask] = useState(null);

  const {
    pendingContextNames,
    pendingProjectNames,
    highlightZoneId,
    handleAddContext,
    handleAddProject,
  } = useInboxPendingTags({ contextDisplayMap, projectDisplayMap });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const durationTasks = useMemo(
    () => getDurationTriageTasks(filteredTasks),
    [filteredTasks],
  );
  const projectTasks = useMemo(
    () => getUntaggedProjectTasks(filteredTasks),
    [filteredTasks],
  );
  const contextTasks = useMemo(
    () => getUntaggedContextTasks(filteredTasks),
    [filteredTasks],
  );

  const durationCount = useMemo(
    () => getDurationTriageTasks(allTasks).length,
    [allTasks],
  );
  const projectCount = useMemo(
    () => getUntaggedProjectTasks(allTasks).length,
    [allTasks],
  );
  const contextCount = useMemo(
    () => getUntaggedContextTasks(allTasks).length,
    [allTasks],
  );

  const groups = useMemo(
    () =>
      buildInboxTriageGroups({
        t,
        durationTasks,
        projectTasks,
        contextTasks,
        durationCount,
        projectCount,
        contextCount,
        projectDisplayMap,
        contextDisplayMap,
      }),
    [
      t,
      durationTasks,
      projectTasks,
      contextTasks,
      durationCount,
      projectCount,
      contextCount,
      projectDisplayMap,
      contextDisplayMap,
    ],
  );

  const safeOpenGroup = Math.min(openGroup, Math.max(0, groups.length - 1));
  const activeGroup = groups[safeOpenGroup] ?? null;

  useEffect(() => {
    onContextPanelOpenChange?.(activeGroup?.key === 'context');
  }, [activeGroup?.key, onContextPanelOpenChange]);

  const detailZones = useMemo(() => {
    if (activeGroup?.key === 'context') {
      return buildContextDropZonesWithPending(
        contextDisplayMap,
        pendingContextNames,
      );
    }
    if (activeGroup?.key === 'project') {
      return buildProjectDropZonesWithPending(
        projectDisplayMap,
        pendingProjectNames,
      );
    }
    return activeGroup?.zones ?? [];
  }, [
    activeGroup,
    contextDisplayMap,
    pendingContextNames,
    projectDisplayMap,
    pendingProjectNames,
  ]);

  const tagAddConfig = useMemo(() => {
    if (activeGroup?.key === 'context') {
      return {
        placeholder: t(
          'inboxview.newContextPlaceholder',
          'Nieuwe context, bijv. @thuis',
        ),
        onAdd: handleAddContext,
        testId: 'inbox-context-add-input',
      };
    }
    if (activeGroup?.key === 'project') {
      return {
        placeholder: t(
          'inboxview.newProjectPlaceholder',
          'Nieuw project, bijv. #werk',
        ),
        onAdd: handleAddProject,
        testId: 'inbox-project-add-input',
      };
    }
    return null;
  }, [activeGroup?.key, t, handleAddContext, handleAddProject]);

  const {
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handleZoneKeyboardAssign,
  } = useInboxViewControllerDrag({
    user,
    updateTask,
    activeGroup,
    pendingProjectNames,
    projectDisplayMap,
    pendingContextNames,
    contextDisplayMap,
    setActiveTask,
  });

  const handlePanelToggle = (index) => (nextOpen) => {
    if (nextOpen) setOpenGroup(index);
  };

  const detailEmptyMessage = useMemo(() => {
    if (!activeGroup) {
      return t(
        'inboxview.selectGroup',
        'Selecteer een groep om zones te tonen',
      );
    }
    return '';
  }, [activeGroup, t]);

  return {
    sensors,
    activeTask,
    groups,
    safeOpenGroup,
    activeGroup,
    detailZones,
    tagAddConfig,
    highlightZoneId,
    detailEmptyMessage,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handlePanelToggle,
    handleZoneKeyboardAssign,
  };
}
